import { useEffect, useRef } from 'react';
import {
  mediaSessionCommand,
  mediaSessionSnapshot,
  type MediaSessionMetadata,
  type MediaSessionTransport,
  type PlayerCommand,
} from '@visious/core';
import {
  createMediaSessionService,
  type MediaSessionService,
} from '../../platform/mediaSession';

/**
 * The React binding: hold a `MediaSessionService` for the life of the mount, publish what this
 * render says, and route an OS button back through the screen's one command dispatcher.
 *
 * Nothing here decides anything. `mediaSessionSnapshot` decides what the session should hold and
 * `mediaSessionCommand` decides what a button means, both in core, both under test — what is left is a
 * lifetime and a ref, which is the same division `useWatchedRecording` and `useWatchProgressRecording`
 * already follow.
 *
 * **It is the third reader of the transport poll and adds no timer** — deliberately.
 * The two numbers it needs are the two the bar is already sampling four times a second; a fourth timer
 * would be a fourth thing to keep in step with a `<video>` that reports `NaN` for the first frames of
 * every load.
 */

export interface MediaSessionBinding {
  /** `null` until the video record has arrived, or where the page is not showing something playable. */
  readonly metadata: MediaSessionMetadata | null;
  /**
   * `null` where the app has no transport of its own — §7's no-MSE arm has no `PlayerPort`, so the
   * browser is the only thing that knows where that element is. See `playback/mediaSession.ts`.
   */
  readonly transport: MediaSessionTransport | null;
  /** The screen's one dispatcher (`runCommand`), so an OS button and a keypress take the same path. */
  readonly command: (command: PlayerCommand) => void;
}

export function useMediaSession(binding: MediaSessionBinding): void {
  /**
   * The dispatcher through a ref, and this is the part that would be a real bug written the obvious way.
   *
   * An action handler is registered **once**, when the action set changes — not on every render — so a
   * handler closed over `binding.command` would keep calling the dispatcher from the render that
   * happened to register it. That dispatcher reads `tracks.video` and `source` out of *its* closure, so
   * a lock-screen press minutes later would frame-step by the wrong frame rate and toggle captions
   * against a track list from before the last recovery reload. The service is created once for the same
   * reason: recreating it per render would clear and re-register every handler four times a second.
   */
  const commandRef = useRef(binding.command);
  useEffect(() => {
    commandRef.current = binding.command;
  });

  const serviceRef = useRef<MediaSessionService | null>(null);

  useEffect(() => {
    const service = createMediaSessionService((action, detail) => {
      const command = mediaSessionCommand(action, detail);
      if (command !== null) commandRef.current(command);
    });
    serviceRef.current = service;

    return () => {
      serviceRef.current = null;
      // The Watch screen is keyed by video, so navigating to another one unmounts this. Releasing here
      // rather than relying on the next mount to overwrite is what keeps the OS from showing a video
      // the reader has left — including when the next page is not a video at all.
      service.release();
    };
  }, []);

  /**
   * Publish after every render, with no dependency list.
   *
   * That looks profligate and is the cheap option: the snapshot is a fresh object every render (the
   * transport is), so any dependency array would compare unequal every time anyway, and the *real*
   * gate is `mediaSessionDiff` — which is where it belongs, because the platform is the only thing that
   * knows how long ago it last handed the position over. An unchanged sample writes nothing.
   *
   * Declared after the mount effect so ordering is guaranteed: effects run in declaration order, so the
   * service exists before the first publish.
   */
  useEffect(() => {
    serviceRef.current?.publish(
      mediaSessionSnapshot({ metadata: binding.metadata, transport: binding.transport }),
    );
  });
}
